import React from "react";
import { useQuery } from "@tanstack/react-query";
import useUiContext from "@/custolink-hooks/useUiContext";
import {
  AssetFrontendCmpFormApi,
  AssetFrontendCmpRegisterProductApi,
} from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";
import Loading from "@/components/shared/Loading";
import Alert from "@/components/ui/Alert/Alert";
import RdaLoading from "@/custolink-views/asset_frontend/redesigns/RdaLoading";
import CustomfieldsForm from "@/custolink-views/asset_frontend/asset_view/asset_components/CustomfieldsForm";
import { useTranslation } from "react-i18next";
import {
  AssetViewDetails,
} from "@/custolink-views/asset_frontend/asset_view/useAssetViewFacade";
import { InfoNote } from "@/custolink-views/asset_frontend/redesigns/formKit";
import { useAssetFrontendDesign } from "@/custolink-views/asset_frontend/redesigns/assetFrontendDesign";

// Shared with the classic design (AssetViewClassic renders this same component inside its
// own Drawer/AssetDrawerScope), so the loading spinner and the already-registered notice
// follow the asset-frontend switch: classic visitors keep the old Loading/Alert, new-design
// visitors get RdaLoading/InfoNote. Everything else in this file (CustomfieldsForm itself)
// was already redesigned unconditionally by an earlier batch.
export default function RegisterProduct(p: {
  componentId: number;
  assetView: AssetViewDetails;
  onDone: () => void;
}) {

  const uic = useUiContext();
  const {t} = useTranslation()
  const newDesign = useAssetFrontendDesign();

  const queryInfo = useQuery({
    queryFn: () => {
      return AssetFrontendCmpRegisterProductApi.getInfo(
        uic._orgId,
        p.assetView.urlParamsForRequests.viewId,
        p.assetView.urlParamsForRequests.viewValue,
        p.assetView.urlParamsForRequests.assetId,
      );
    },
    queryKey: [
      "AssetFrontendCmpRegisterProductApi",
      AssetFrontendCmpRegisterProductApi.getInfo.name,
      uic._orgId+"_"+p.assetView.urlParamsForRequests.keyString,
    ],
  });

  const queryFields = useQuery({
    queryFn: () =>
      AssetFrontendCmpFormApi.getCustomfields(
        uic.organisationContext.organisationId,
        p.componentId,
      ),
    queryKey: [
      "AssetFrontendCmpFormApi",
      "getFields",
      uic.organisationContext.organisationId,
      p.componentId,
    ],
    enabled: queryInfo.data?.alreadyRegisteredByThisUser == false,
  });

  if (queryInfo.isPending) {
    return newDesign ? <RdaLoading /> : <Loading loading={true} />;
  }

  if (queryInfo?.data?.alreadyRegisteredByThisUser === true) {
    return newDesign ? (
      <InfoNote>
        {t('component.registerproduct.alert.productAlreadyRegistered')}
      </InfoNote>
    ) : (
      <Alert type={"info"}>
        {t('component.registerproduct.alert.productAlreadyRegistered')}
      </Alert>
    );
  }

  if (queryFields.isPending) {
    return newDesign ? <RdaLoading /> : <Loading loading={true} />;
  }

  if (queryFields.data != null) {
    return (
      <CustomfieldsForm
        submitText={t("component.registerproduct.submitText")}
        topAlert={t("component.registerproduct.alert.pleaseRegisterProductBelow")}
        assetViewDetails={p.assetView}
        componentId={p.componentId}
        fields={queryFields.data.fields}
        i18nprefix={queryFields.data?.i18nprefix ?? ""}
        onDone={p.onDone}
      />
    );
  }

  return <></>;
}
